import type { TileMap } from './tilemap';
import { HUMAN_FRIENDLY_AI } from './humanFriendlyAi';
import type { Fighter, FighterInput, Vec3 } from './types';

/** Backpedalling is slower than running so that chasing beats fleeing. */
export const BACKPEDAL_FACTOR = 0.55;
export const STRAFE_FACTOR = 0.8;

/** The dash that replaced the jump: a short burst along the way of travel (D-16, E6). */
export const DASH_SPEED = 14;
export const DASH_TIME = 0.18;
export const DASH_COOLDOWN = 1.6;

/** How quickly a fighter reaches its wish velocity on normal ground; ice lowers it. */
const GROUND_FRICTION = 1;
/**
 * The least grip any tile gives. A tile of no friction at all would hold a fighter that stopped
 * on it where it stood, able to leave only by dashing.
 */
export const MIN_FRICTION = 0.05;
/**
 * `1 / BATTLE_DT`, kept as a literal so physics owes nothing to the step loop. At full friction
 * that reaches the wish velocity within one fixed step, so ordinary ground steers exactly as
 * instantly as it did before tiles had friction at all — which is what the arena AI's kiting and
 * holding distances were tuned against. Only a low-friction tile slides.
 */
const ACCELERATION = 30;
const EPSILON = 1e-4;

function movementPace(input: FighterInput): number {
  const pace = Math.max(0.5, Math.min(1.25, input.speedScale ?? 1));
  return input.humanFriendlyAi === true ? pace * HUMAN_FRIENDLY_AI.movement : pace;
}

/** Direction a yaw/pitch pair looks along (yaw 0 = +z). */
export function forwardVector(yaw: number, pitch: number): Vec3 {
  const c = Math.cos(pitch);
  return { x: Math.sin(yaw) * c, y: Math.sin(pitch), z: Math.cos(yaw) * c };
}

/** Desired horizontal velocity from an input, in world space. */
export function desiredVelocity(f: Fighter, input: FighterInput): { x: number; z: number } {
  const mz = clamp(input.moveZ);
  const mx = clamp(input.moveX);
  const speed = f.speed * movementPace(input);
  const forwardSpeed = mz >= 0 ? speed : speed * BACKPEDAL_FACTOR;
  const fx = Math.sin(f.yaw);
  const fz = Math.cos(f.yaw);
  const rx = Math.cos(f.yaw);
  const rz = -Math.sin(f.yaw);
  const x = fx * mz * forwardSpeed + rx * mx * speed * STRAFE_FACTOR;
  const z = fz * mz * forwardSpeed + rz * mx * speed * STRAFE_FACTOR;
  const len = Math.hypot(x, z);
  const max = Math.max(forwardSpeed, speed * STRAFE_FACTOR);
  return len > max ? { x: (x / len) * max, z: (z / len) * max } : { x, z };
}

/**
 * Moves a fighter across the tilemap for one tick: axis-separated AABB sweeps against the tiles
 * its movement class cannot cross and the bodies of the `others` (D-35), the tile's own speed and
 * friction underfoot, and the dash.
 *
 * There is one walkable ground plane in v1 (D-17), so `pos.y` is the fighter's standing height
 * and never changes: no gravity, no step-up, nothing to fall off.
 */
export function moveFighter(
  map: TileMap,
  f: Fighter,
  input: FighterInput,
  dt: number,
  others: readonly Fighter[] = [],
): void {
  const tile = map.tileAt(f.pos.x, f.pos.z);
  const friction = Math.max(MIN_FRICTION, tile.friction ?? GROUND_FRICTION);

  f.dashCooldown = Math.max(0, f.dashCooldown - dt);
  f.dashTime = Math.max(0, f.dashTime - dt);
  if (f.dashTime === 0) f.dashDir = null;

  if (f.stun > 0) {
    // Hit stun kills a dash outright: being staggered mid-lunge should cost the lunge.
    f.dashTime = 0;
    f.dashDir = null;
    f.vel.x *= 0.8;
    f.vel.z *= 0.8;
  } else {
    const wish = desiredVelocity(f, input);
    if (input.dash && f.dashCooldown === 0) {
      const len = Math.hypot(wish.x, wish.z);
      // Dashing while standing still lunges the way the fighter faces.
      const dir =
        len > EPSILON
          ? { x: wish.x / len, z: wish.z / len }
          : { x: Math.sin(f.yaw), z: Math.cos(f.yaw) };
      f.dashDir = dir;
      f.dashTime = DASH_TIME;
      f.dashCooldown = DASH_COOLDOWN;
    }
    if (f.dashTime > 0 && f.dashDir) {
      // Difficulty scales all locomotion, not just walking. An unscaled dash
      // disproportionately lets slow-level pursuers catch ranged opponents.
      const pace = movementPace(input) * (f.pace ?? 1);
      f.vel.x = f.dashDir.x * DASH_SPEED * pace;
      f.vel.z = f.dashDir.z * DASH_SPEED * pace;
    } else {
      // Low friction slides: the fighter keeps its old velocity and steers only slowly.
      const k = Math.min(1, ACCELERATION * friction * dt);
      const scale = tile.speedScale ?? 1;
      f.vel.x += (wish.x * scale - f.vel.x) * k;
      f.vel.z += (wish.z * scale - f.vel.z) * k;
    }
  }

  sweepAxis(map, f, 'x', f.vel.x * dt, others);
  sweepAxis(map, f, 'z', f.vel.z * dt, others);
}

/** Returns false when the move was cut short by a tile the fighter cannot cross, or a body. */
function sweepAxis(
  map: TileMap,
  f: Fighter,
  axis: 'x' | 'z',
  delta: number,
  others: readonly Fighter[],
): boolean {
  if (delta === 0) return true;
  const stopped = (feet: Vec3) => overlapsBlocked(map, feet, f) || bumpsInto(feet, f, others);
  const target = f.pos[axis] + delta;
  const probe: Vec3 = { ...f.pos, [axis]: target };
  if (!stopped(probe)) {
    f.pos[axis] = target;
    return true;
  }
  // Move up to the face of whatever stopped it.
  const dir = Math.sign(delta);
  let low = 0;
  let high = Math.abs(delta);
  for (let i = 0; i < 10; i++) {
    const mid = (low + high) / 2;
    const p: Vec3 = { ...f.pos, [axis]: f.pos[axis] + dir * mid };
    if (stopped(p)) high = mid;
    else low = mid;
  }
  f.pos[axis] += dir * Math.max(0, low - EPSILON);
  f.vel[axis] = 0;
  // Running into a wall ends a dash rather than pinning the fighter against it for the duration.
  f.dashTime = 0;
  f.dashDir = null;
  return false;
}

/**
 * Whether a fighter stepping to `feet` would walk into another's body (D-35). A body is a circle
 * of its half-width on the one ground plane, at whatever height it flies (D-17), so two fighters
 * meet where their reaches across the floor are measured. A step that leaves the two no closer is
 * always free: two that somehow stand inside each other can still part.
 */
export function bumpsInto(
  feet: Vec3,
  f: Pick<Fighter, 'pos' | 'halfWidth'>,
  others: readonly Fighter[],
): boolean {
  for (const other of others) {
    if (other === f || !other.alive) continue;
    const reach = f.halfWidth + other.halfWidth;
    const after = Math.hypot(feet.x - other.pos.x, feet.z - other.pos.z);
    if (after >= reach) continue;
    if (after < Math.hypot(f.pos.x - other.pos.x, f.pos.z - other.pos.z)) return true;
  }
  return false;
}

/** Whether a fighter's footprint at `feet` covers any tile its movement class cannot cross. */
export function overlapsBlocked(
  map: TileMap,
  feet: Vec3,
  f: Pick<Fighter, 'halfWidth' | 'movement'>,
): boolean {
  const x0 = Math.floor(feet.x - f.halfWidth + EPSILON);
  const x1 = Math.floor(feet.x + f.halfWidth - EPSILON);
  const z0 = Math.floor(feet.z - f.halfWidth + EPSILON);
  const z1 = Math.floor(feet.z + f.halfWidth - EPSILON);
  for (let z = z0; z <= z1; z++)
    for (let x = x0; x <= x1; x++) if (!map.passable(map.tile(x, z), f.movement)) return true;
  return false;
}

function clamp(v: number): number {
  return Math.max(-1, Math.min(1, v));
}
